// The current-production hero: the old static "CURRENT PRODUCTION" plank and the
// "still deploying / failed" warning strip fused into one living banner. The
// active skin draws the scene (see skins.tsx); this component owns the state
// machine and the deploy metadata HUD laid over it.
//
// State: `idle` shows the live prod commit (the resting scene). A not-yet-live
// promote drives `pending`/`failure`. When prod flips to a new SHA we flash
// `success` briefly, then settle back to `idle` on the new commit.
import { useEffect, useRef, useState } from 'react';
import type { CommitInfo, ReleaseRequest } from './api';
import { ProductIdentity } from './ProductIdentity';
import type { TargetInfo } from './api';
import { SKINS, heroCenterPct, type HeroState, type SkinId } from './skins';

// Idle chatter: famous/cliché words on peace, rotated while nothing is shipping.
// Universal across skins; the pending/failure/success lines come from the skin.
const PEACE_QUOTES = [
  '“Give peace a chance.” — Lennon',
  '“Peace begins with a smile.” — Mother Teresa',
  '“Blessed are the peacemakers.”',
  '“Peace is the only battle worth waging.” — Camus',
  '“Let there be peace on earth.”',
  '“An eye for an eye leaves the whole world blind.”',
  'All quiet. The realm is at peace.',
];

const DEFAULT_REPO = 'Bike4Mind/bike4mind';
const PROFILE_BASE = 'https://github.com';
const sha7 = (s: string) => s.slice(0, 7);
const SUCCESS_FLASH_MS = 7000;

/** Pure map from the not-live promote status to a base hero state (idle otherwise). */
export function resolveHeroState(
  promotion: { sha: string; state: string } | undefined,
  currentProdSha: string | null,
): HeroState {
  if (promotion && promotion.sha !== currentProdSha) {
    if (promotion.state === 'pending') return 'pending';
    if (promotion.state === 'failure') return 'failure';
  }
  return 'idle';
}

// Links: anything clickable IS a link (commit, PR, deploy run, GitHub profiles).
function ShaLink({ sha, repo }: { sha: string; repo: string }) {
  return <a className="hud-sha" href={`https://github.com/${repo}/commit/${sha}`} target="_blank" rel="noreferrer">{sha7(sha)}</a>;
}
function User({ login }: { login: string }) {
  return <a href={`${PROFILE_BASE}/${login}`} target="_blank" rel="noreferrer">@{login}</a>;
}
function RunLink({ href, label }: { href: string; label: string }) {
  return (
    <a className="hud-run" href={href} target="_blank" rel="noreferrer">
      <img className="hud-run-bow" src="/icon-bow.png" width={16} height={16} alt="" aria-hidden />
      {label}
    </a>
  );
}
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.round((Date.now() - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const min = Math.round(sec / 60), hr = Math.round(min / 60), day = Math.round(hr / 24);
  if (Math.abs(sec) < 60) return rtf.format(-sec, 'second');
  if (Math.abs(min) < 60) return rtf.format(-min, 'minute');
  if (Math.abs(hr) < 24) return rtf.format(-hr, 'hour');
  return rtf.format(-day, 'day');
}

export function PromoteHero({
  currentProd, prodCommit, promotion, promotionReq, skin, repo = DEFAULT_REPO, prodDeployedAt, product,
}: {
  product?: Pick<TargetInfo, 'id' | 'label' | 'logo'>;
  currentProd: ReleaseRequest | null;
  prodCommit: CommitInfo | null;
  promotion?: { sha: string; state: string };
  /** The release request behind an in-flight/failed promote (requester, time, run). */
  promotionReq?: ReleaseRequest | null;
  skin: SkinId;
  /** `owner/repo` the current target's commit/PR links point at. */
  repo?: string;
  /** The Deployment's own timestamp for `currentProd` (e.g. polaris): current
   * prod there can predate the console entirely, so there may be no request to
   * carry a "promoted by" time at all. */
  prodDeployedAt?: string;
}) {
  const currentProdSha = currentProd?.sha ?? null;
  const base = resolveHeroState(promotion, currentProdSha);

  // Flash "just shipped" when prod flips to a new SHA (not on first load).
  const [flash, setFlash] = useState(false);
  const prevSha = useRef<string | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (prevSha.current && currentProdSha && prevSha.current !== currentProdSha) {
      setFlash(true);
      timer = setTimeout(() => setFlash(false), SUCCESS_FLASH_MS);
    }
    prevSha.current = currentProdSha;
    return () => { if (timer) clearTimeout(timer); };
  }, [currentProdSha]);
  const state: HeroState = flash && base === 'idle' ? 'success' : base;

  // Cosmetic progress creep while questing (no real % exists). Frozen elsewhere.
  const [progress, setProgress] = useState(0.1);
  const progressRef = useRef(0.1);
  progressRef.current = progress;
  useEffect(() => {
    if (state !== 'pending') {
      setProgress(state === 'success' ? 1 : state === 'failure' ? Math.max(progressRef.current, 0.42) : 0.1);
      return;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setProgress(0.5); return; }
    setProgress(0.1);
    const id = setInterval(() => setProgress(p => Math.min(0.9, p + (0.9 - p) * 0.06 + 0.004)), 240);
    return () => clearInterval(id);
  }, [state]);

  // Rotate the peace quotes while idle; other states have a fixed line.
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    if (state !== 'idle') return;
    const id = setInterval(() => setQuoteIdx(i => (i + 1) % PEACE_QUOTES.length), 6000);
    return () => clearInterval(id);
  }, [state]);
  const bubbleText = state === 'idle' ? PEACE_QUOTES[quoteIdx % PEACE_QUOTES.length] : SKINS[skin].lines[state];
  const bubbleLeft = heroCenterPct(state, progress);

  const Scene = SKINS[skin].PromoteScene;
  const pct = Math.round(progress * 100);
  const live = state === 'idle' || state === 'success';
  const runUrl = live ? (prodCommit?.runUrl ?? currentProd?.runUrl) : promotionReq?.runUrl;

  const eyebrow = state === 'pending' ? 'Promoting → Production'
    : state === 'failure' ? 'Promote Waylaid'
    : state === 'success' ? 'Just Shipped'
    : 'Current Production';
  const badge = state === 'pending' ? { cls: 'badge-enroute', text: 'EN ROUTE' }
    : state === 'failure' ? { cls: 'badge-failed', text: 'FAILED' }
    : currentProd || state === 'success' ? { cls: 'badge-live', text: 'LIVE' }
    : null;

  return (
    <div className="hero" data-state={state} data-testid="prod-banner">
      <div className="hero-scene">
        <div className="hero-eyebrow">
          <span>{eyebrow}</span>
          {badge && <span className={`badge ${badge.cls}`} data-testid={badge.cls === 'badge-live' ? 'prod-live' : undefined}>{badge.text}</span>}
        </div>

        <Scene state={state} progress={progress} />

        <div className="hero-bubble" style={{ left: `${bubbleLeft}%` }} aria-hidden>{bubbleText}</div>
      </div>

      <div className="hero-hud">
        {product && <ProductIdentity key={product.id} product={product} placement="production" />}
        {live ? (
          currentProd ? (
            <>
              <div className="hud-main">
                <ShaLink sha={currentProd.sha} repo={repo} />
                {prodCommit?.subject && <span className="hud-subject" title={prodCommit.subject}>{prodCommit.subject}</span>}
                {runUrl && <RunLink href={runUrl} label="prod deploy ↗" />}
              </div>
              <div className="hud-meta" data-testid="prod-meta">
                {prodCommit?.author && (
                  <>
                    by <User login={prodCommit.author} />
                    {prodCommit.pr != null && <> . <a href={`https://github.com/${repo}/pull/${prodCommit.pr}`} target="_blank" rel="noreferrer">#{prodCommit.pr}</a></>}
                    {prodCommit.authoredDate && <> . committed <span title={new Date(prodCommit.authoredDate).toLocaleString()}>{relativeTime(prodCommit.authoredDate)}</span></>}
                  </>
                )}
                {/* "promoted by" needs an actual console request behind it - current
                    prod can predate the console entirely (e.g. polaris), in which
                    case fall back to the Deployment's own timestamp instead. */}
                {currentProd.requester ? (
                  <>
                    {prodCommit?.author && ' . '}
                    promoted by <User login={currentProd.requester} />
                    {currentProd.createdAt ? ` . ${new Date(currentProd.createdAt).toLocaleString()}` : ''}
                  </>
                ) : prodDeployedAt ? (
                  <>
                    {prodCommit?.author && ' . '}
                    deployed <span title={new Date(prodDeployedAt).toLocaleString()}>{relativeTime(prodDeployedAt)}</span>
                  </>
                ) : null}
                {state === 'success' && <> . <em>live just now</em> 🍺</>}
              </div>
            </>
          ) : (
            <div className="hud-main">
              <span className="hud-sha hud-sha-unknown">unknown</span>
              <span className="hud-subject">no promotion recorded this session</span>
            </div>
          )
        ) : (
          <>
            <div className="hud-main">
              {promotion && <ShaLink sha={promotion.sha} repo={repo} />}
              <span className="hud-subject">{state === 'pending' ? 'deploying to production…' : 'deploy failed on the road'}</span>
              {runUrl && <RunLink href={runUrl} label={state === 'pending' ? 'deploy run ↗' : 'failed run ↗'} />}
            </div>
            <div className="hud-meta" data-testid="prod-meta">
              {promotionReq?.requester && <>promoted by <User login={promotionReq.requester} /> . </>}
              {state === 'pending'
                ? <em>questing to prod ({pct}%)</em>
                : <em>waylaid at {pct}% — deploy failed</em>}
              {currentProdSha && <> . prod still live: <ShaLink sha={currentProdSha} repo={repo} /></>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
